import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  createUser,
  getUser,
  getUsers,
  editUser,
  deleteUser,
} from "api/userApi"
import { uploadImages, deleteImage } from "api/imageApi"
import { useAuth } from "hooks/authHook"

export const USERS_QUERY_KEY = "users"
const AUTH_VALIDATE_KEY = ["auth", "validate"]

export const useUsers = (params = {}) => {
  return useQuery({
    queryKey: [USERS_QUERY_KEY, params], // include params in the query key for caching
    queryFn: () => getUsers(params),
    keepPreviousData: true, // optional, useful for pagination or filtering
  })
}

export const useUser = identifier => {
  return useQuery({
    queryKey: [USERS_QUERY_KEY, identifier], // unique cache key
    queryFn: () => getUser(identifier),
    enabled: !!identifier, // only run if identifier is provided
  })
}

export const useCreateUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createUser,
    onSuccess: data => {
      if (data?.success) {
        queryClient.invalidateQueries([USERS_QUERY_KEY]) // refresh users list
      }
    },
  })
}

export const useEditUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: editUser,
    onSuccess: data => {
      if (data?.success) {
        queryClient.invalidateQueries([USERS_QUERY_KEY]) // refresh users list
        // Also refresh the current user in the auth cache (header avatar, profile pages)
        queryClient.invalidateQueries(AUTH_VALIDATE_KEY)
      }
    },
  })
}

export const useDeleteUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteUser,
    onSuccess: data => {
      if (data?.success) {
        queryClient.invalidateQueries([USERS_QUERY_KEY]) // refresh users list
      }
    },
  })
}

/**
 * Upload a new profile picture for the currently logged-in user.
 * Uploads the file via /images, swaps the user's profile_image to the new asset,
 * and removes the previous one in the same multipart request when possible.
 */
export const useUpdateProfileImage = () => {
  const queryClient = useQueryClient()
  const { user: currentUser } = useAuth()

  return useMutation({
    mutationFn: async file => {
      if (!file) throw new Error("No file provided")
      const userId = currentUser?.id
      if (!userId) throw new Error("User not authenticated")

      const formData = new FormData()
      formData.append("images[]", file)

      const previousImageId =
        Array.isArray(currentUser?.profile_image) &&
        currentUser.profile_image[0]?.id
          ? currentUser.profile_image[0].id
          : null
      if (previousImageId) {
        formData.append("delete_file_ids[]", previousImageId)
      }

      const uploadRes = await uploadImages(formData)
      const newImage = uploadRes?.data?.[0]
      if (!newImage?.url) {
        throw new Error(uploadRes?.message || "Image upload failed")
      }

      const editRes = await editUser({
        id: userId,
        profile_image: [newImage],
      })
      if (!editRes?.success) {
        throw new Error(editRes?.message || "Failed to update profile image")
      }
      return newImage
    },
    onSuccess: () => {
      queryClient.invalidateQueries([USERS_QUERY_KEY])
      queryClient.invalidateQueries(AUTH_VALIDATE_KEY)
    },
  })
}

/**
 * Remove the current user's profile picture. Sets profile_image to null on the user
 * record and best-effort deletes the underlying image asset.
 */
export const useDeleteProfileImage = () => {
  const queryClient = useQueryClient()
  const { user: currentUser } = useAuth()

  return useMutation({
    mutationFn: async () => {
      const userId = currentUser?.id
      if (!userId) throw new Error("User not authenticated")

      const previousImageId =
        Array.isArray(currentUser?.profile_image) &&
        currentUser.profile_image[0]?.id
          ? currentUser.profile_image[0].id
          : null

      const editRes = await editUser({ id: userId, profile_image: null })
      if (!editRes?.success) {
        throw new Error(editRes?.message || "Failed to remove profile image")
      }

      if (previousImageId) {
        try {
          await deleteImage(previousImageId)
        } catch (error) {
          // Non-fatal: image is already detached from the user.
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries([USERS_QUERY_KEY])
      queryClient.invalidateQueries(AUTH_VALIDATE_KEY)
    },
  })
}
